import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, Spinner, useToast,
  type DataGridColumn,
} from '@kss/ui';
import {
  apiClient, apiResources, apiResourcesByGroup,
  type ApiFieldMeta, type ApiResourceMeta,
} from '@kss/api';
import {
  ArrowLeft, Download, Eye, GripVertical, Plus, Save, Trash2, Wand2,
} from 'lucide-react';

/**
 * Report builder.
 *
 * Power-user tool for assembling ad-hoc reports against any of the 280+
 * registered live resources. The result is saved as a `ReportDefinition`
 * row that the panel can rerun, schedule and email.
 *
 * Layout — single screen with five sections:
 *   1. Header     — name, code, description, resource picker
 *   2. Columns    — available fields (left) → selected (right, reorderable)
 *   3. Filters    — column / operator / value rows
 *   4. Sort & limit
 *   5. Preview    — live fetch + client-side filter/sort/limit, with CSV export
 *
 * The whole spec is stored as JSON inside `ReportDefinition.config` so the
 * UI can rebuild itself when the row is reopened. A backend that wants to
 * run the same query server-side can read the same JSON.
 */

type Op = 'eq' | 'ne' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'isNull' | 'notNull';

const OP_LABELS: Record<Op, string> = {
  eq: 'equals',
  ne: 'not equal',
  contains: 'contains',
  gt: 'greater than',
  gte: '≥',
  lt: 'less than',
  lte: '≤',
  between: 'between',
  isNull: 'is empty',
  notNull: 'is not empty',
};

const NUMERIC_OPS: Op[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'isNull', 'notNull'];
const STRING_OPS:  Op[] = ['eq', 'ne', 'contains', 'isNull', 'notNull'];
const BOOL_OPS:    Op[] = ['eq', 'ne'];
const DATE_OPS:    Op[] = ['eq', 'gt', 'gte', 'lt', 'lte', 'between', 'isNull', 'notNull'];

interface FilterRule {
  field: string;
  op: Op;
  value: string;
  value2?: string; // for between
}

interface ReportSpec {
  resource: string;        // ApiResourceMeta.name
  group: string;           // ApiResourceMeta.group
  columns: string[];       // selected field names, in order
  filters: FilterRule[];
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-9 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function ReportBuilder() {
  const { id: rawId } = useParams<{ id?: string }>();
  const isEdit = !!rawId && rawId !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  // Header
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');

  // Spec
  const [resource, setResource] = useState<string>('');
  const [group, setGroup] = useState<string>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState<number>(100);

  // Preview
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  // Save
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The full backend row, kept so an edit-save can send a COMPLETE PUT
  // (the backend PUT is a full replace — a partial PUT NULLs every other
  // column, including reportType/category we don't surface in the UI).
  const [rawRow, setRawRow] = useState<Record<string, unknown>>({});

  // Hydrate when editing.
  useEffect(() => {
    if (!isEdit || !rawId) return;
    apiClient
      .get(`/reporting/ReportDefinition/${encodeURIComponent(rawId)}`)
      .then((r) => {
        const d = (r.data?.data ?? r.data) as Record<string, unknown>;
        if (!d || typeof d !== 'object') return;
        setRawRow(d);
        setName(String(d.name ?? ''));
        setCode(String(d.code ?? ''));
        setDescription(String(d.description ?? ''));
        // The spec is stored in layoutJson (preferred) / queryDefinition; older
        // rows used a (non-existent) `config` column. Parse whichever holds it.
        let cfg: ReportSpec | null = null;
        for (const raw of [d.layoutJson, d.queryDefinition, d.config]) {
          if (typeof raw === 'string' && raw.trim()) {
            try { cfg = JSON.parse(raw) as ReportSpec; break; } catch { /* try next */ }
          } else if (raw && typeof raw === 'object') {
            cfg = raw as ReportSpec; break;
          }
        }
        if (cfg) {
          setResource(cfg.resource ?? '');
          setGroup(cfg.group ?? String(d.category ?? ''));
          setSelected(cfg.columns ?? []);
          setFilters(cfg.filters ?? []);
          setSortField(cfg.sortField ?? '');
          setSortDir(cfg.sortDir ?? 'asc');
          setLimit(cfg.limit ?? 100);
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Load failed'));
  }, [isEdit, rawId]);

  // Resource meta for the chosen resource.
  const meta = useMemo<ApiResourceMeta | undefined>(
    () => apiResources.find((r) => r.name === resource && r.group === group),
    [resource, group],
  );

  const fieldMap = useMemo(() => {
    const m = new Map<string, ApiFieldMeta>();
    for (const f of meta?.fields ?? []) m.set(f.name, f);
    return m;
  }, [meta]);

  const available = useMemo(
    () => (meta?.fields ?? []).filter((f) => !selected.includes(f.name)),
    [meta, selected],
  );

  // Drag-and-drop column reorder (within selected list).
  const [dragField, setDragField] = useState<string | null>(null);

  function addColumn(name: string) {
    setSelected((cur) => (cur.includes(name) ? cur : [...cur, name]));
  }
  function removeColumn(name: string) {
    setSelected((cur) => cur.filter((n) => n !== name));
  }
  function moveSelected(name: string, beforeName: string | null) {
    setSelected((cur) => {
      const without = cur.filter((n) => n !== name);
      if (beforeName == null) return [...without, name];
      const idx = without.indexOf(beforeName);
      if (idx < 0) return [...without, name];
      return [...without.slice(0, idx), name, ...without.slice(idx)];
    });
  }

  function addFilter() {
    if (!meta?.fields?.length) return;
    const first = meta.fields[0]!;
    setFilters((cur) => [...cur, { field: first.name, op: defaultOpFor(first), value: '' }]);
  }
  function updateFilter(idx: number, patch: Partial<FilterRule>) {
    setFilters((cur) => cur.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }
  function removeFilter(idx: number) {
    setFilters((cur) => cur.filter((_, i) => i !== idx));
  }

  // Live preview — fetch resource list and apply transforms client-side.
  async function runPreview() {
    if (!meta) return;
    setPreviewing(true);
    setPreviewErr(null);
    try {
      const r = await apiClient.get(meta.basePath);
      const list = pickList(r.data);
      const filtered = list.filter((row) => filters.every((f) => evaluateFilter(row, f, fieldMap)));
      const sorted = sortField
        ? [...filtered].sort((a, b) => compare(a[sortField], b[sortField]) * (sortDir === 'desc' ? -1 : 1))
        : filtered;
      setRows(sorted.slice(0, Math.max(1, limit)));
    } catch (e) {
      setPreviewErr(e instanceof Error ? e.message : 'Preview failed');
      setRows([]);
    } finally {
      setPreviewing(false);
    }
  }

  function exportCsv() {
    if (selected.length === 0 || rows.length === 0) {
      toast.error('Nothing to export', 'Pick columns and run a preview first.');
      return;
    }
    const header = selected.join(',');
    const body = rows
      .map((r) => selected.map((k) => csvCell(r[k])).join(','))
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(code || name || 'report').replace(/[^A-Za-z0-9_-]/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function save() {
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    if (!meta) {
      setErr('Pick a data source first.');
      return;
    }
    if (selected.length === 0) {
      setErr('Select at least one column.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const spec: ReportSpec = {
        resource: meta.name,
        group: meta.group,
        columns: selected,
        filters,
        sortField: sortField || undefined,
        sortDir,
        limit,
      };
      const specJson = JSON.stringify(spec);
      // Persist the spec into REAL ReportDefinition columns. The entity has no
      // `config`/`sourceResource`/`sourceGroup` columns — the backend silently
      // dropped those, so saved reports came back empty. layoutJson is the
      // canonical home; mirror to queryDefinition so whichever the backend
      // echoes round-trips. `category` carries the group for the list page.
      const payload = {
        name,
        code: code || undefined,
        description: description || undefined,
        reportType: 'Tabular',
        category: meta.group,
        queryType: 'Json',
        layoutJson: specJson,
        queryDefinition: specJson,
        isActive: 1,
      };
      if (isEdit && rawId) {
        // Full-replace PUT: fold the complete existing row in first so we don't
        // NULL columns the UI doesn't surface.
        await apiClient.put('/reporting/ReportDefinition', { ...rawRow, ...payload, id: rawId });
        toast.success('Saved', `${name} updated`);
      } else {
        await apiClient.post('/reporting/ReportDefinition', payload);
        toast.success('Saved', `${name} created`);
      }
      navigate('/panel/reports');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // DataGrid columns derived from the selected list + the resource's fieldMap.
  // Cell value lookup is tolerant: the registry field name (swagger camelCase)
  // can differ in casing from the JSON key the live endpoint returns, which
  // previously rendered blank cells "even when the value is filled". Try the
  // exact key, then a case-insensitive match, then a nested `data` envelope.
  const pickVal = (r: Record<string, unknown>, name: string): unknown => {
    if (r[name] !== undefined) return r[name];
    const lc = name.toLowerCase();
    for (const k of Object.keys(r)) if (k.toLowerCase() === lc) return r[k];
    const nested = r.data as Record<string, unknown> | undefined;
    if (nested && typeof nested === 'object' && nested[name] !== undefined) return nested[name];
    return undefined;
  };
  const previewColumns = useMemo<DataGridColumn<Record<string, unknown>>[]>(
    () =>
      selected.map((name) => ({
        accessor: name,
        title: name,
        sortable: true,
        render: (r: Record<string, unknown>) => formatCell(pickVal(r, name)),
      })),
    [selected],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/reports')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit report' : 'New report'}
          </h1>
          <p className="text-xs text-content-muted">
            {meta ? `${meta.group} · ${meta.name}` : 'Pick a data source to start.'}
            {selected.length > 0 && ` · ${selected.length} column(s)`}
            {filters.length > 0 && ` · ${filters.length} filter(s)`}
          </p>
        </div>
        <Button variant="outline" disabled={previewing || !meta} onClick={runPreview}>
          <Eye className="h-4 w-4" /> {previewing ? 'Running…' : 'Preview'}
        </Button>
        <Button onClick={save} disabled={saving}>
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save report'}
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* Header card */}
      <Card>
        <h3 className="mb-3 text-sm font-semibold text-content-primary">1 · Definition</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">
              Name <span className="text-danger">*</span>
            </span>
            <input
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Top customers by revenue"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Code</span>
            <input
              className={inputCls}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="RPT-CUST-TOP"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Group · Resource</span>
            <div className="grid grid-cols-2 gap-2">
              <select
                className={selectCls}
                value={group}
                onChange={(e) => {
                  setGroup(e.target.value);
                  setResource('');
                  setSelected([]);
                  setFilters([]);
                }}
              >
                <option value="">— group —</option>
                {Object.keys(apiResourcesByGroup).sort().map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              <select
                className={selectCls}
                value={resource}
                onChange={(e) => setResource(e.target.value)}
                disabled={!group}
              >
                <option value="">— resource —</option>
                {(apiResourcesByGroup[group] ?? []).map((r) => (
                  <option key={r.name} value={r.name}>{r.name}</option>
                ))}
              </select>
            </div>
          </label>
          <label className="flex flex-col gap-1 md:col-span-3">
            <span className="text-xs font-medium text-content-secondary">Description</span>
            <input
              className={inputCls}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Visible on the saved-reports list"
            />
          </label>
        </div>
      </Card>

      {/* Columns picker */}
      <Card>
        <h3 className="mb-3 text-sm font-semibold text-content-primary">2 · Columns</h3>
        {!meta ? (
          <div className="p-3 text-sm text-content-muted">Pick a resource first.</div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">
                Available ({available.length})
              </div>
              <div className="flex flex-wrap gap-1.5 rounded-md border border-border bg-surface-card p-2">
                {available.length === 0 ? (
                  <span className="text-xs text-content-muted">All fields selected.</span>
                ) : (
                  available.map((f) => (
                    <button
                      key={f.name}
                      type="button"
                      onClick={() => addColumn(f.name)}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-card-hover px-2 py-0.5 text-xs hover:border-primary"
                    >
                      <Plus size={11} /> {f.name}
                      <span className="text-[9px] text-content-muted">{f.kind}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">
                Selected ({selected.length}) · drag to reorder
              </div>
              <div
                className="min-h-[80px] rounded-md border border-dashed border-border bg-surface-card p-2"
                onDragOver={(e) => { if (dragField) e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragField) moveSelected(dragField, null);
                  setDragField(null);
                }}
              >
                {selected.length === 0 ? (
                  <span className="text-xs text-content-muted">
                    Click any field on the left to add it.
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selected.map((n) => (
                      <span
                        key={n}
                        draggable
                        onDragStart={() => setDragField(n)}
                        onDragOver={(e) => { if (dragField && dragField !== n) e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragField && dragField !== n) moveSelected(dragField, n);
                          setDragField(null);
                        }}
                        onDragEnd={() => setDragField(null)}
                        className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2 py-0.5 text-xs text-primary"
                      >
                        <GripVertical size={11} className="cursor-grab" />
                        {n}
                        <button
                          type="button"
                          onClick={() => removeColumn(n)}
                          className="text-primary/70 hover:text-danger"
                          aria-label={`Remove ${n}`}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">3 · Filters</h3>
          <Button size="sm" variant="outline" onClick={addFilter} disabled={!meta}>
            <Plus className="h-4 w-4" /> Add filter
          </Button>
        </div>
        {!meta ? (
          <div className="text-sm text-content-muted">Pick a resource first.</div>
        ) : filters.length === 0 ? (
          <div className="text-xs text-content-muted">
            No filters — the report returns the first {limit} row(s) of the resource.
          </div>
        ) : (
          <div className="space-y-2">
            {filters.map((f, i) => {
              const field = fieldMap.get(f.field);
              const ops = field ? opsForKind(field) : STRING_OPS;
              const needsValue2 = f.op === 'between';
              const noValue = f.op === 'isNull' || f.op === 'notNull';
              return (
                <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-surface-card p-2">
                  <select
                    className={`${selectCls} flex-1`}
                    value={f.field}
                    onChange={(e) => {
                      const next = e.target.value;
                      const newField = fieldMap.get(next);
                      const newOps = newField ? opsForKind(newField) : STRING_OPS;
                      updateFilter(i, {
                        field: next,
                        op: newOps.includes(f.op) ? f.op : newOps[0]!,
                      });
                    }}
                    style={{ minWidth: 180, maxWidth: 240 }}
                  >
                    {(meta.fields ?? []).map((ff) => (
                      <option key={ff.name} value={ff.name}>{ff.name}</option>
                    ))}
                  </select>
                  <select
                    className={selectCls}
                    value={f.op}
                    onChange={(e) => updateFilter(i, { op: e.target.value as Op })}
                    style={{ width: 140 }}
                  >
                    {ops.map((o) => (
                      <option key={o} value={o}>{OP_LABELS[o]}</option>
                    ))}
                  </select>
                  {!noValue && (
                    <input
                      className={inputCls}
                      value={f.value}
                      onChange={(e) => updateFilter(i, { value: e.target.value })}
                      placeholder="value"
                      style={{ flex: 1, minWidth: 120 }}
                    />
                  )}
                  {needsValue2 && (
                    <input
                      className={inputCls}
                      value={f.value2 ?? ''}
                      onChange={(e) => updateFilter(i, { value2: e.target.value })}
                      placeholder="and"
                      style={{ flex: 1, minWidth: 120 }}
                    />
                  )}
                  <button
                    type="button"
                    className="text-content-muted hover:text-danger"
                    onClick={() => removeFilter(i)}
                    aria-label="Remove filter"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Sort + limit */}
      <Card>
        <h3 className="mb-3 text-sm font-semibold text-content-primary">4 · Sort &amp; limit</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Sort by</span>
            <select
              className={selectCls}
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              disabled={!meta}
            >
              <option value="">— none —</option>
              {(meta?.fields ?? []).map((f) => (
                <option key={f.name} value={f.name}>{f.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Direction</span>
            <select
              className={selectCls}
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}
              disabled={!sortField}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Limit (rows)</span>
            <input
              type="number"
              className={inputCls}
              value={limit}
              min={1}
              max={10000}
              onChange={(e) => setLimit(Math.max(1, Math.min(10000, Number(e.target.value) || 100)))}
            />
          </label>
        </div>
      </Card>

      {/* Preview */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">
            5 · Preview {rows.length ? `· ${rows.length} row(s)` : ''}
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={!rows.length} onClick={exportCsv}>
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
            <Button size="sm" disabled={previewing || !meta} onClick={runPreview}>
              <Wand2 className="h-3.5 w-3.5" /> {previewing ? 'Running…' : 'Run'}
            </Button>
          </div>
        </div>
        {previewErr && <Alert variant="danger" className="mb-3">{previewErr}</Alert>}
        {previewing && rows.length === 0 ? (
          <div className="flex items-center gap-2 p-4 text-sm text-content-muted">
            <Spinner /> Fetching {meta?.basePath}…
          </div>
        ) : selected.length === 0 ? (
          <div className="p-4 text-sm text-content-muted">
            Pick at least one column, then run.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-content-muted">
            No data yet. Click <em>Run</em> to fetch the resource and apply your filters.
          </div>
        ) : (
          <DataGrid<Record<string, unknown>>
            data={rows}
            columns={previewColumns}
            rowId={
              // meta.idField is often 'code', but live rows can have an empty
              // code (e.g. SalesInvoice) → duplicate React keys + blank rows.
              // Use the configured id field only when every row has a non-empty
              // value for it; otherwise fall back to the numeric 'id'.
              ((rows.length && meta?.idField && rows.every((r) => String(pickVal(r, meta.idField) ?? '') !== ''))
                ? meta.idField
                : 'id') as keyof Record<string, unknown> & string
            }
          />
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function defaultOpFor(f: ApiFieldMeta): Op {
  return opsForKind(f)[0]!;
}

function opsForKind(f: ApiFieldMeta): Op[] {
  if (f.kind === 'boolean') return BOOL_OPS;
  if (f.kind === 'number') return NUMERIC_OPS;
  if (f.kind === 'datetime') return DATE_OPS;
  return STRING_OPS;
}

function evaluateFilter(
  row: Record<string, unknown>,
  f: FilterRule,
  fieldMap: Map<string, ApiFieldMeta>,
): boolean {
  const raw = row[f.field];
  const meta = fieldMap.get(f.field);
  const kind = meta?.kind ?? 'string';

  if (f.op === 'isNull') return raw == null || raw === '';
  if (f.op === 'notNull') return !(raw == null || raw === '');

  if (kind === 'number' || kind === 'datetime') {
    const v = kind === 'number' ? Number(raw) : Date.parse(String(raw ?? ''));
    const a = kind === 'number' ? Number(f.value) : Date.parse(f.value || '');
    const b = kind === 'number' ? Number(f.value2 ?? 0) : Date.parse(f.value2 ?? '');
    if (Number.isNaN(v)) return false;
    switch (f.op) {
      case 'eq':  return v === a;
      case 'ne':  return v !== a;
      case 'gt':  return v > a;
      case 'gte': return v >= a;
      case 'lt':  return v < a;
      case 'lte': return v <= a;
      case 'between': return v >= Math.min(a, b) && v <= Math.max(a, b);
      default:    return true;
    }
  }

  if (kind === 'boolean') {
    const v = !!raw && raw !== 'false' && raw !== 0;
    const a = f.value === 'true' || f.value === '1';
    return f.op === 'eq' ? v === a : v !== a;
  }

  // Strings
  const v = String(raw ?? '').toLowerCase();
  const a = String(f.value ?? '').toLowerCase();
  switch (f.op) {
    case 'eq':       return v === a;
    case 'ne':       return v !== a;
    case 'contains': return v.includes(a);
    default:         return true;
  }
}

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return String(a).localeCompare(String(b));
}

function formatCell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
  return String(v);
}

function csvCell(v: unknown): string {
  if (v == null) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function pickList(d: unknown): Record<string, unknown>[] {
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as Record<string, unknown>[];
    if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as Record<string, unknown>[];
    }
  }
  return [];
}
